import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'

import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'

// Components
import {Button} from '@salesforce/retail-react-app/app/components/shared/ui'

// Hooks
import {useGTM} from '@app/hooks/use-gtm'
import {useToast} from './use-toast'
import {useWishList} from '@app/hooks/use-wish-list'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

// Constants
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST
} from '@app/constants'

/**
 * Custom hook to manage wishlist actions such as adding and removing products.
 * This hook integrates with Google Tag Manager (GTM) and provides toast notifications
 * for user feedback. It handles asynchronous operations for wishlist management
 * and supports queueing of products when the wishlist is not immediately available.
 *
 * @returns {Object} - An object containing the wishlist data, loading state, and functions
 *                     to add or remove products from the wishlist.
 * @property {Object} wishlist - The current state of the user's wishlist.
 * @property {boolean} isWishlistLoading - Indicates if the wishlist is currently loading.
 * @property {Function} addProductToWishlist - Function to add a product to the wishlist.
 * @property {Function} removeProductFromWishlist - Function to remove a product from the wishlist.
 */
export const useWishlistActions = () => {
    const {triggerAddtoWishListEvent} = useGTM()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const [addToWishlistQueue, setAddToWishlistQueue] = useState([])

    /**
     * When the wishlist finally loaded, we will add the products from the queue to the wishlist.
     */
    useEffect(() => {
        if (!wishlist || addToWishlistQueue.length === 0) return

        const productsInWishlist =
            wishlist.customerProductListItems?.map((product) => product.productId) ?? []

        addToWishlistQueue.map((product) => {
            // If the product is already in the wishlist after the user logged in, we don't need to add it.
            if (productsInWishlist.includes(product.productId || product.id)) return

            addProductToWishlist(product)
        })

        setAddToWishlistQueue([])
    }, [addToWishlistQueue, wishlist])

    /**************** Mutation Actions ****************/
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const addProductToWishlist = async (product) => {
        /**
         * When the wishlist is not yet loaded, we will add the product to the queue.
         * This happens when a logged out user tries to add a product to the wishlist.
         * The log in popup opens, the user logs in, and the wishlist is not imediatly available.
         */
        if (!wishlist) {
            setAddToWishlistQueue((prev) => [...prev, product])
            return
        }

        const listId = wishlist.id
        return await createCustomerProductListItem(
            {
                parameters: {customerId, listId},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product.productId || product.id
                }
            },
            {
                onError: () => {
                    toast({
                        description: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        description: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                        status: 'success',
                        action: (
                            // it would be better if we could use <Button as={Link}>
                            // but unfortunately the Link component is not compatible
                            // with Chakra Toast, since the ToastManager is rendered via portal
                            // and the toast doesn't have access to intl provider, which is a
                            // requirement of the Link component.
                            <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                                {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                            </Button>
                        )
                    })

                    /* GTM Integration */
                    triggerAddtoWishListEvent(product)
                }
            }
        )
    }

    const removeProductFromWishlist = async (product) => {
        const listId = wishlist.id
        const itemId = wishlist.customerProductListItems.find(
            (i) => i.productId === (product.productId || product.id)
        ).id

        return await deleteCustomerProductListItem(
            {
                body: {},
                parameters: {customerId, listId, itemId}
            },
            {
                onError: () => {
                    toast({
                        description: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        description: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST),
                        status: 'success'
                    })
                }
            }
        )
    }

    return {
        wishlist,
        isWishlistLoading,
        addProductToWishlist,
        removeProductFromWishlist
    }
}
